import { useRef, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { useAuth } from '@/pages/auth';

const INTENTIONAL_LOGOUT_KEY = 'yk-intentional-logout';

/**
 * Set by `requestAccountDeletion` / explicit "Sign out" button so the next
 * `RequireAuth` redirect knows the unauthed flip was deliberate and skips
 * the "session expired" warning.
 */
export function markIntentionalLogout() {
  try {
    sessionStorage.setItem(INTENTIONAL_LOGOUT_KEY, '1');
  } catch {
    /* ignore */
  }
}

function takeIntentionalLogout(): boolean {
  try {
    const v = sessionStorage.getItem(INTENTIONAL_LOGOUT_KEY);
    if (v) sessionStorage.removeItem(INTENTIONAL_LOGOUT_KEY);
    return !!v;
  } catch {
    return false;
  }
}

interface RequireAuthProps {
  children: ReactNode;
  /**
   * When set, anonymous visitors get THIS rendered instead of being
   * redirected to `/login`. Used on routes we want to hide from
   * non-members entirely (e.g. `/tv/*` where the standard redirect
   * advertised the feature's existence to crawlers and indexers).
   *
   * Pass a `<NotFound />` element here to make the route behave as if
   * it doesn't exist from an unauthed viewpoint — matching what payment
   * gateway bots and search crawlers should see.
   *
   * Authenticated viewers still see `children` as before.
   */
  fallback?: ReactNode;
}

const SESSION_EXPIRED_MESSAGE = 'Your session expired. Please sign in again.';

/**
 * Auth gate for protected routes.
 *
 * - While the persisted session is still being restored (`status` is `idle` or
 *   `loading` — `init()` runs on app boot in `App.tsx`) we show a centered
 *   spinner instead of redirecting, so a logged-in user isn't bounced to
 *   `/login` on a hard refresh.
 * - When unauthenticated (or no token) we redirect to `/login`, capturing the
 *   current location in `state.from` so Login can return the user there. If
 *   the user was previously authenticated in this mount (i.e. their session
 *   just expired), we also pass a `state.warning` string so Login can render
 *   a "Your session expired" banner above the form.
 * - When authenticated we render the protected children.
 */
export function RequireAuth({ children, fallback }: RequireAuthProps) {
  const location = useLocation();
  const status = useAuth((s) => s.status);
  const token = useAuth((s) => s.token);

  // Track whether *this gate* has seen the user authenticated. If status
  // later flips to `unauthenticated` (or the token disappears) we treat that
  // as a session expiry and surface a warning on the Login page. A first-time
  // unauthenticated hit (no prior authed snapshot) just navigates silently.
  const wasAuthedRef = useRef(false);
  if (status === 'authenticated' && token) {
    wasAuthedRef.current = true;
  }

  if (status === 'idle' || status === 'loading') {
    return (
      <div
        className="flex min-h-[60vh] items-center justify-center"
        role="status"
        aria-label="Loading"
      >
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (status === 'unauthenticated' || !token) {
    // When a `fallback` is provided the route opts OUT of the
    // redirect-to-login behaviour and renders the fallback (typically a
    // 404 page) instead. This hides the route from anonymous visitors
    // entirely — useful for routes whose mere existence is a compliance
    // signal we don't want crawlers / underwriters to pick up.
    if (fallback !== undefined) {
      return <>{fallback}</>;
    }
    const intentional = takeIntentionalLogout();
    const expired = wasAuthedRef.current && !intentional;
    return (
      <Navigate
        to="/login"
        replace
        state={
          expired
            ? { from: location, warning: SESSION_EXPIRED_MESSAGE }
            : { from: location }
        }
      />
    );
  }

  return <>{children}</>;
}
